import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  TablePagination,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search,
  FilterAltOff,
  Refresh,
  Visibility,
  CheckCircle,
  WarningAmber,
  Close,
  Email,
  Phone,
  Person,
  Business,
  Send,
  ForwardToInbox,
  MarkEmailRead,
  ScheduleSend,
  QuestionAnswer,
  CalendarToday,
  AdminPanelSettings,
  Category,
  Contacts as ContactsIcon,
} from '@mui/icons-material';
import {
  contactInquiriesAPI,
  citiesAPI,
  categoriesAPI,
  contactsAPI,
} from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageContacts } from '../../utils/roleUtils';

/** Formats ISO string into localized human-readable date & time */
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
};

/** Formats status string into clean Title Case words */
const formatStatusLabel = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val).trim();
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

/** Get visual config (color, background, border, icon, label) for progressStatus */
const getProgressStatusConfig = (status) => {
  if (status === null || status === undefined || String(status).trim() === '') {
    return null;
  }
  const raw = String(status).trim();
  const lower = raw.toLowerCase().replace(/[\s_-]+/g, '');

  // Handle numeric or string enum values
  if (lower === '0' || lower === 'new' || lower === 'open' || lower === 'submitted') {
    return {
      label: lower === '0' ? 'New' : formatStatusLabel(raw),
      color: '#0369a1',
      bgColor: '#e0f2fe',
      borderColor: '#bae6fd',
      icon: <ScheduleSend sx={{ fontSize: '14px !important', color: '#0284c7 !important' }} />,
    };
  }
  if (
    lower === '1' ||
    lower === 'inprogress' ||
    lower === 'pending' ||
    lower === 'processing' ||
    lower === 'underreview' ||
    lower === 'contacted'
  ) {
    return {
      label: lower === '1' ? 'In Progress' : formatStatusLabel(raw),
      color: '#b45309',
      bgColor: '#fef3c7',
      borderColor: '#fde68a',
      icon: <WarningAmber sx={{ fontSize: '14px !important', color: '#d97706 !important' }} />,
    };
  }
  if (
    lower === '2' ||
    lower === 'resolved' ||
    lower === 'completed' ||
    lower === 'done' ||
    lower === 'approved' ||
    lower === 'released'
  ) {
    return {
      label: lower === '2' ? 'Completed' : formatStatusLabel(raw),
      color: '#15803d',
      bgColor: '#dcfce7',
      borderColor: '#bbf7d0',
      icon: <CheckCircle sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />,
    };
  }
  if (
    lower === '3' ||
    lower === 'closed' ||
    lower === 'cancelled' ||
    lower === 'canceled' ||
    lower === 'rejected'
  ) {
    return {
      label: lower === '3' ? 'Closed' : formatStatusLabel(raw),
      color: '#b91c1c',
      bgColor: '#fee2e2',
      borderColor: '#fecaca',
      icon: <Close sx={{ fontSize: '14px !important', color: '#dc2626 !important' }} />,
    };
  }

  return {
    label: formatStatusLabel(raw),
    color: '#0F3D3E',
    bgColor: '#ebf4f4',
    borderColor: '#cce5e5',
    icon: null,
  };
};

const ContactInquiries = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canManage = canManageContacts(adminUser?.role);

  // Data & loading state
  const [inquiries, setInquiries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);

  // Dropdown filter options (City, Category, Contact)
  const [selectedCityId, setSelectedCityId] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [contactsList, setContactsList] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [isFetchingContacts, setIsFetchingContacts] = useState(false);

  // Pagination (MUI 0-indexed)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [releaseStatusFilter, setReleaseStatusFilter] = useState('all'); // 'all', 'true', 'false'
  const [contactVerifiedFilter, setContactVerifiedFilter] = useState('all'); // 'all', 'true', 'false'

  // Dialogs state
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [inquiryToRelease, setInquiryToRelease] = useState(null);

  // Toast / Snackbar
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  // Debounced search query
  const debouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = (searchQuery || '').trim();
      if (debouncedSearchRef.current !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  // Load Contacts list according to selected city & category
  const fetchContactsForFilter = useCallback(async (cityId, categoryId = '') => {
    setIsFetchingContacts(true);
    try {
      const res = await contactsAPI.getContacts(
        cityId || null,
        categoryId || null,
        0,
        1000
      );
      let list = [];
      if (res?.data?.payload && Array.isArray(res.data.payload)) {
        list = res.data.payload;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      }
      setContactsList(list);
    } catch (err) {
      console.error('Error fetching contacts for filter:', err);
      setContactsList([]);
    } finally {
      setIsFetchingContacts(false);
    }
  }, []);

  // Initial load: Fetch Cities & Categories with hasContact=true
  useEffect(() => {
    const initDropdowns = async () => {
      setIsFetchingCategories(true);
      try {
        let cityId = 1;
        const citiesRes = await citiesAPI.getCities();
        if (
          citiesRes?.data?.success &&
          Array.isArray(citiesRes.data.payload) &&
          citiesRes.data.payload.length > 0
        ) {
          cityId = citiesRes.data.payload[0].id;
        }
        setSelectedCityId(cityId);

        // Fetch categories for this city with contact support
        const catRes = await categoriesAPI.getCategories(cityId, 0, 100, {
          hasContact: true,
        });
        if (catRes?.data?.success && Array.isArray(catRes.data.payload)) {
          const contactCategories = catRes.data.payload.filter(
            (c) => c.hasContact === true || c.hasContact === 'true'
          );
          setCategories(contactCategories);
        }

        // Fetch initial contacts
        fetchContactsForFilter(cityId, '');
      } catch (err) {
        console.error('Error initializing category/contact filter:', err);
      } finally {
        setIsFetchingCategories(false);
      }
    };

    initDropdowns();
  }, [fetchContactsForFilter]);

  // Handle Category selection change (cascades to Contact dropdown)
  const handleCategoryChange = (e) => {
    const newCatId = e.target.value;
    setSelectedCategoryId(newCatId);
    setSelectedContactId(''); // Reset contact filter on category change
    setPage(0);
    fetchContactsForFilter(selectedCityId, newCatId);
  };

  // Handle Contact selection change
  const handleContactChange = (e) => {
    const newContactId = e.target.value;
    setSelectedContactId(newContactId);
    setPage(0);
  };

  // Fetch inquiries from API
  const fetchInquiries = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiOptions = {
        page: page + 1, // backend API is 1-based
        pageSize: rowsPerPage,
        search: debouncedSearch,
      };

      if (releaseStatusFilter !== 'all') {
        apiOptions.isReleased = releaseStatusFilter === 'true';
      }
      if (contactVerifiedFilter !== 'all') {
        apiOptions.isContactVerified = contactVerifiedFilter === 'true';
      }
      if (selectedContactId && String(selectedContactId).trim() !== '' && selectedContactId !== 'all') {
        const parsed = parseInt(selectedContactId, 10);
        if (!isNaN(parsed)) {
          apiOptions.contactId = parsed;
        }
      }

      const res = await contactInquiriesAPI.getContactInquiries(apiOptions);
      if (res?.data?.success) {
        const payload = res.data.payload;
        const items = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
        setInquiries(items);
        const total = payload?.total ?? res.data.total ?? items.length;
        setTotalCount(Number(total) >= 0 ? Number(total) : items.length);
      } else {
        setInquiries([]);
        setTotalCount(0);
        const msg =
          res?.data?.errorMessage ||
          res?.data?.message ||
          t('contactInquiries.loadFailed') ||
          'Failed to load inquiries.';
        showToast(msg, 'error');
      }
    } catch (err) {
      console.error('Error fetching contact inquiries:', err);
      setInquiries([]);
      setTotalCount(0);
      const errMsg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load inquiries.';
      showToast(errMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    releaseStatusFilter,
    contactVerifiedFilter,
    selectedContactId,
    t,
  ]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  // Handle release inquiry action
  const handleOpenReleaseDialog = (inquiry) => {
    setInquiryToRelease(inquiry);
    setReleaseDialogOpen(true);
  };

  const handleCloseReleaseDialog = () => {
    setInquiryToRelease(null);
    setReleaseDialogOpen(false);
  };

  const handleConfirmRelease = async () => {
    if (!inquiryToRelease?.inquiryId) return;
    setIsReleasing(true);
    const targetId = inquiryToRelease.inquiryId;
    try {
      const res = await contactInquiriesAPI.releaseContactInquiry(targetId);
      if (res?.data?.success) {
        showToast(
          'Contact inquiry has been released and dispatched successfully!',
          'success'
        );
        handleCloseReleaseDialog();
        if (selectedInquiry?.inquiryId === targetId) {
          setSelectedInquiry((prev) => ({
            ...prev,
            isReleased: true,
            releasedAtUtc: new Date().toISOString(),
            releasedByAdminName: adminUser?.firstName || 'Admin',
          }));
        }
        fetchInquiries();
      } else {
        const msg = res?.data?.errorMessage || res?.data?.message || 'Failed to release inquiry.';
        showToast(msg, 'error');
      }
    } catch (err) {
      console.error('Error releasing contact inquiry:', err);
      const errMsg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to release inquiry.';
      showToast(errMsg, 'error');
    } finally {
      setIsReleasing(false);
    }
  };

  // Open details modal
  const handleOpenDetails = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedInquiry(null);
  };

  // Reset all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedCategoryId('');
    setSelectedContactId('');
    setReleaseStatusFilter('all');
    setContactVerifiedFilter('all');
    setPage(0);
    if (selectedCityId) {
      fetchContactsForFilter(selectedCityId, '');
    }
  };

  // Quick stats computed from current view
  const releasedCount = inquiries.filter((i) => i.isReleased).length;
  const pendingReleaseCount = inquiries.filter((i) => !i.isReleased).length;
  const unverifiedContactsCount = inquiries.filter((i) => !i.isContactVerified).length;

  return (
    <Box p={1}>
      {/* Page Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('contactInquiries.title') || 'Contact Inquiries'}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchInquiries}
          disabled={isLoading}
          sx={{
            borderColor: '#0F3D3E',
            color: '#0F3D3E',
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            px: 2.5,
            py: 0.8,
            '&:hover': {
              borderColor: '#0a2829',
              backgroundColor: '#ebf4f4',
            },
          }}
        >
          {t('common.refresh') || 'Refresh'}
        </Button>
      </Box>

      {/* Metric / Stat Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('contactInquiries.statTotal') || 'Total Inquiries'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E', mt: 0.5 }}>
                    {totalCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#ebf4f4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0F3D3E',
                  }}
                >
                  <QuestionAnswer fontSize="medium" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('contactInquiries.statReleased') || 'Released (Current View)'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#16a34a', mt: 0.5 }}>
                    {releasedCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#16a34a',
                  }}
                >
                  <MarkEmailRead fontSize="medium" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              setReleaseStatusFilter('false');
              setPage(0);
            }}
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #fed7aa',
              background: 'linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)',
              cursor: 'pointer',
              transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(249,115,22,0.12)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('contactInquiries.statPending') || 'Pending Release'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ea580c', mt: 0.5 }}>
                    {pendingReleaseCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#ffedd5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ea580c',
                  }}
                >
                  <ScheduleSend fontSize="medium" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #ffffff 0%, #fefce8 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#a16207', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('contactInquiries.statUnverified') || 'Unverified Contacts'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ca8a04', mt: 0.5 }}>
                    {unverifiedContactsCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#fef9c3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ca8a04',
                  }}
                >
                  <WarningAmber fontSize="medium" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter & Search Bar with Category & Contact Cascading Dropdowns */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Keyword Search */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                t('contactInquiries.searchPlaceholder') ||
                'Search by sender, email, message...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
            />
          </Grid>

          {/* Category Dropdown Filter */}
          <Grid item xs={12} sm={6} md={2.2}>
            <FormControl fullWidth size="small">
              <InputLabel id="inquiries-filter-category-label">
                {t('contactInquiries.filterCategory') || 'Category'}
              </InputLabel>
              <Select
                labelId="inquiries-filter-category-label"
                value={selectedCategoryId}
                label={t('contactInquiries.filterCategory') || 'Category'}
                onChange={handleCategoryChange}
                disabled={isFetchingCategories}
              >
                <MenuItem value="">
                  <em>{t('contactInquiries.allCategories') || 'All Categories'}</em>
                </MenuItem>
                {categories.map((cat) => {
                  const catTitle = cat.title_EN || cat.title || cat.name || `Category #${cat.id}`;
                  return (
                    <MenuItem key={cat.id} value={cat.id}>
                      {catTitle}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Contact Dropdown Filter (filtered by Category) */}
          <Grid item xs={12} sm={6} md={2.3}>
            <FormControl fullWidth size="small">
              <InputLabel id="inquiries-filter-contact-label">
                {t('contactInquiries.filterContact') || 'Contact'}
              </InputLabel>
              <Select
                labelId="inquiries-filter-contact-label"
                value={selectedContactId}
                label={t('contactInquiries.filterContact') || 'Contact'}
                onChange={handleContactChange}
                disabled={isFetchingContacts}
              >
                <MenuItem value="">
                  <em>{t('contactInquiries.allContacts') || 'All Contacts'}</em>
                </MenuItem>
                {contactsList.map((contact) => {
                  const title =
                    contact.title ||
                    contact.contactTitle ||
                    contact.contactPerson ||
                    contact.name ||
                    'Contact';
                  return (
                    <MenuItem key={contact.id} value={contact.id}>
                      {title}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Release Status filter */}
          <Grid item xs={12} sm={6} md={1.7}>
            <FormControl fullWidth size="small">
              <InputLabel id="release-status-select-label">
                {t('contactInquiries.filterReleaseStatus') || 'Release Status'}
              </InputLabel>
              <Select
                labelId="release-status-select-label"
                value={releaseStatusFilter}
                label={t('contactInquiries.filterReleaseStatus') || 'Release Status'}
                onChange={(e) => {
                  setReleaseStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('contactInquiries.all') || 'All Statuses'}</MenuItem>
                <MenuItem value="true">{t('contactInquiries.released') || 'Released'}</MenuItem>
                <MenuItem value="false">{t('contactInquiries.unreleased') || 'Pending Release'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Contact Verification filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="contact-verified-select-label">
                {t('contactInquiries.filterContactVerification') || 'Contact Verification'}
              </InputLabel>
              <Select
                labelId="contact-verified-select-label"
                value={contactVerifiedFilter}
                label={t('contactInquiries.filterContactVerification') || 'Contact Verification'}
                onChange={(e) => {
                  setContactVerifiedFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('contactInquiries.allVerification') || 'All Verification'}</MenuItem>
                <MenuItem value="true">{t('contactInquiries.verified') || 'Verified'}</MenuItem>
                <MenuItem value="false">{t('contactInquiries.unverified') || 'Unverified'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters Button */}
          <Grid
            item
            xs={12}
            md={1.3}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={{
                ...clearFilterButtonSx,
                borderRadius: 1,
                width: '100%',
                minWidth: 'auto',
                maxWidth: 'none',
              }}
            >
              {t('common.clear') || 'Clear'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Main Table Container */}
      {isLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" my={8}>
          <CircularProgress sx={{ color: '#0F3D3E', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {t('common.loading') || 'Loading contact inquiries...'}
          </Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #f3f4f6',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table
              sx={{
                minWidth: 1100,
                '& .MuiTableCell-head': {
                  backgroundColor: '#ebf4f4',
                  color: '#0F3D3E',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  py: 1.8,
                  px: 2,
                },
                '& .MuiTableCell-body': {
                  py: 1.8,
                  px: 2,
                  fontSize: '0.875rem',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell width="220px">{t('contactInquiries.colContact') || 'Recipient Contact'}</TableCell>
                  <TableCell width="200px">{t('contactInquiries.colSender') || 'Sender'}</TableCell>
                  <TableCell>{t('contactInquiries.colMessage') || 'Message'}</TableCell>
                  <TableCell width="160px">{t('contactInquiries.colCreatedAt') || 'Received Date'}</TableCell>
                  <TableCell width="140px">{t('contactInquiries.colContactVerified') || 'Contact Status'}</TableCell>
                  <TableCell width="140px">{t('contactInquiries.colReleaseStatus') || 'Release Status'}</TableCell>
                  <TableCell width="150px">{t('contactInquiries.colProgressStatus') || 'Progress Status'}</TableCell>
                  <TableCell width="120px" align="center">{t('contactInquiries.colActions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <QuestionAnswer sx={{ fontSize: 48, color: '#9ca3af', mb: 1.5 }} />
                        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                          {t('contactInquiries.noInquiriesFound') || 'No contact inquiries found'}
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                          Try adjusting your search criteria or resetting filters
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  inquiries.map((inquiry) => {
                    const senderDisplayName =
                      (inquiry.senderName || `${inquiry.firstName || ''} ${inquiry.surname || ''}`).trim() ||
                      'User';

                    return (
                      <TableRow
                        key={inquiry.inquiryId}
                        sx={{
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: '#f9fafb',
                          },
                        }}
                      >
                        {/* Recipient Contact Info (NO ID displayed) */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                              {inquiry.contactTitle || 'Contact'}
                            </Typography>
                            {inquiry.contactPerson && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 14, color: '#6b7280' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {inquiry.contactPerson}
                                </Typography>
                              </Box>
                            )}
                            {inquiry.contactEmail && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email sx={{ fontSize: 14, color: '#6b7280' }} />
                                <Typography
                                  component="a"
                                  href={`mailto:${inquiry.contactEmail}`}
                                  variant="caption"
                                  sx={{
                                    color: '#0F3D3E',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {inquiry.contactEmail}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* Sender Info (NO ID displayed) */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                              {senderDisplayName}
                            </Typography>
                            {inquiry.senderEmail && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email sx={{ fontSize: 14, color: '#6b7280' }} />
                                <Typography
                                  component="a"
                                  href={`mailto:${inquiry.senderEmail}`}
                                  variant="caption"
                                  sx={{
                                    color: '#0F3D3E',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {inquiry.senderEmail}
                                </Typography>
                              </Box>
                            )}
                            {inquiry.phoneNumber && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone sx={{ fontSize: 14, color: '#6b7280' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {inquiry.phoneNumber}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* Message */}
                        <TableCell>
                          <Box sx={{ maxWidth: 320 }}>
                            <Tooltip
                              title={inquiry.message || inquiry.shortDescription || '—'}
                              arrow
                              placement="top-start"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1f2937',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: 1.4,
                                }}
                              >
                                {inquiry.message || inquiry.shortDescription || '—'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Received Date */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 14, color: '#6b7280' }} />
                            <Typography variant="caption" color="text.primary" sx={{ fontWeight: 500 }}>
                              {formatDateTime(inquiry.createdAtUtc)}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Contact Verified Status */}
                        <TableCell>
                          {inquiry.isContactVerified ? (
                            <Chip
                              size="small"
                              icon={<CheckCircle sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />}
                              label={t('contactInquiries.verified') || 'Verified'}
                              sx={{
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<WarningAmber sx={{ fontSize: '14px !important', color: '#d97706 !important' }} />}
                              label={t('contactInquiries.unverified') || 'Unverified'}
                              sx={{
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Release Status */}
                        <TableCell>
                          {inquiry.isReleased ? (
                            <Tooltip
                              title={
                                inquiry.releasedAtUtc
                                  ? `Released on ${formatDateTime(inquiry.releasedAtUtc)}${
                                      inquiry.releasedByAdminName ? ` by ${inquiry.releasedByAdminName}` : ''
                                    }`
                                  : 'Released'
                              }
                              arrow
                            >
                              <Chip
                                size="small"
                                icon={<MarkEmailRead sx={{ fontSize: '14px !important', color: '#059669 !important' }} />}
                                label={t('contactInquiries.released') || 'Released'}
                                sx={{
                                  backgroundColor: '#d1fae5',
                                  color: '#065f46',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              size="small"
                              icon={<ScheduleSend sx={{ fontSize: '14px !important', color: '#ea580c !important' }} />}
                              label={t('contactInquiries.unreleased') || 'Pending Release'}
                              sx={{
                                backgroundColor: '#ffedd5',
                                color: '#9a3412',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Progress Status */}
                        <TableCell>
                          {(() => {
                            const rawStatus =
                              inquiry.progressStatus ??
                              inquiry.ProgressStatus ??
                              inquiry.progress_status ??
                              inquiry.progress;
                            const config = getProgressStatusConfig(rawStatus);

                            if (!config) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              );
                            }

                            return (
                              <Chip
                                size="small"
                                icon={config.icon}
                                label={config.label}
                                sx={{
                                  backgroundColor: config.bgColor,
                                  color: config.color,
                                  border: `1px solid ${config.borderColor}`,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            );
                          })()}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                            {/* View Details button */}
                            <Tooltip title={t('contactInquiries.btnViewDetails') || 'View Details'} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(inquiry)}
                                sx={{
                                  color: '#0F3D3E',
                                  backgroundColor: '#ebf4f4',
                                  '&:hover': { backgroundColor: '#d5e8e8' },
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Release action button (icon only) */}
                            {!inquiry.isReleased && canManage && (
                              <Tooltip
                                title={t('contactInquiries.btnRelease') || 'Release'}
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenReleaseDialog(inquiry)}
                                  sx={{
                                    color: '#ffffff',
                                    backgroundColor: '#0F3D3E',
                                    '&:hover': {
                                      backgroundColor: '#0a2829',
                                    },
                                  }}
                                >
                                  <ForwardToInbox fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Release Confirmation Dialog (NO ID displayed) */}
      <Dialog
        open={releaseDialogOpen}
        onClose={isReleasing ? undefined : handleCloseReleaseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              backgroundColor: '#ffedd5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ea580c',
            }}
          >
            <ForwardToInbox fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('contactInquiries.releaseDialogTitle') || 'Release Contact Inquiry'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            {t('contactInquiries.releaseDialogNotice') ||
              'Releasing this inquiry will immediately dispatch the inquiry email to the contact with the sender message.'}
          </Alert>

          {inquiryToRelease && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: '#f9fafb',
                borderColor: '#e5e7eb',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Recipient Contact
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="#0F3D3E">
                    {inquiryToRelease.contactTitle || 'Contact'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {inquiryToRelease.contactEmail || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Sender
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {inquiryToRelease.senderName ||
                      `${inquiryToRelease.firstName || ''} ${inquiryToRelease.surname || ''}`.trim() ||
                      'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {inquiryToRelease.senderEmail || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatDateTime(inquiryToRelease.createdAtUtc)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Message
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.3 }}>
                    "{inquiryToRelease.message || inquiryToRelease.shortDescription || '-'}"
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseReleaseDialog}
            disabled={isReleasing}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              color: '#4b5563',
            }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRelease}
            disabled={isReleasing}
            startIcon={isReleasing ? <CircularProgress size={16} color="inherit" /> : <ForwardToInbox sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: '#0F3D3E',
              color: '#ffffff',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              px: 2.5,
              '&:hover': {
                backgroundColor: '#0a2829',
              },
            }}
          >
            {isReleasing
              ? t('contactInquiries.btnReleasing') || 'Releasing...'
              : t('contactInquiries.btnConfirmRelease') || 'Confirm & Release'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inquiry Details Modal (NO ID displayed) */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                backgroundColor: '#ebf4f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0F3D3E',
              }}
            >
              <QuestionAnswer />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                {t('contactInquiries.detailsTitle') || 'Inquiry Details'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Received on {formatDateTime(selectedInquiry?.createdAtUtc)}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDetails} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2.5 }}>
          {selectedInquiry && (
            <Stack spacing={3}>
              {/* Status Badges */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                  Statuses:
                </Typography>

                {selectedInquiry.isContactVerified ? (
                  <Chip
                    size="small"
                    icon={<CheckCircle sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />}
                    label="Contact Verified"
                    sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<WarningAmber sx={{ fontSize: '14px !important', color: '#d97706 !important' }} />}
                    label="Contact Unverified"
                    sx={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                  />
                )}

                {selectedInquiry.isReleased ? (
                  <Chip
                    size="small"
                    icon={<MarkEmailRead sx={{ fontSize: '14px !important', color: '#059669 !important' }} />}
                    label="Released & Dispatched"
                    sx={{ backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<ScheduleSend sx={{ fontSize: '14px !important', color: '#ea580c !important' }} />}
                    label="Pending Release"
                    sx={{ backgroundColor: '#ffedd5', color: '#9a3412', fontWeight: 600 }}
                  />
                )}

                {(() => {
                  const rawStatus =
                    selectedInquiry.progressStatus ??
                    selectedInquiry.ProgressStatus ??
                    selectedInquiry.progress_status ??
                    selectedInquiry.progress;
                  const config = getProgressStatusConfig(rawStatus);
                  if (!config) return null;
                  return (
                    <Chip
                      size="small"
                      icon={config.icon}
                      label={`Progress: ${config.label}`}
                      sx={{
                        backgroundColor: config.bgColor,
                        color: config.color,
                        border: `1px solid ${config.borderColor}`,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  );
                })()}
              </Box>

              {/* Grid: Recipient Contact & Sender (Clean, No IDs) */}
              <Grid container spacing={2.5}>
                {/* Contact Card */}
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Business sx={{ color: '#0F3D3E', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                          {t('contactInquiries.contactDetails') || 'Recipient Contact'}
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5 }} />

                      <Stack spacing={1.2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Contact Title</Typography>
                          <Typography variant="body2" fontWeight={600}>{selectedInquiry.contactTitle || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                          <Typography variant="body2">{selectedInquiry.contactPerson || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Contact Email</Typography>
                          <Typography
                            component="a"
                            href={`mailto:${selectedInquiry.contactEmail}`}
                            variant="body2"
                            sx={{ color: '#0F3D3E', fontWeight: 500, display: 'block' }}
                          >
                            {selectedInquiry.contactEmail || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sender Card */}
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Person sx={{ color: '#0F3D3E', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                          {t('contactInquiries.senderDetails') || 'Sender Information'}
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5 }} />

                      <Stack spacing={1.2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Sender Name</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {selectedInquiry.senderName ||
                              `${selectedInquiry.firstName || ''} ${selectedInquiry.surname || ''}`.trim() ||
                              'User'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Sender Email</Typography>
                          <Typography
                            component="a"
                            href={`mailto:${selectedInquiry.senderEmail}`}
                            variant="body2"
                            sx={{ color: '#0F3D3E', fontWeight: 500, display: 'block' }}
                          >
                            {selectedInquiry.senderEmail || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                          <Typography variant="body2">{selectedInquiry.phoneNumber || 'Not provided'}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Inquiry Message Box */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Email sx={{ color: '#0F3D3E', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                      {t('contactInquiries.inquiryContent') || 'Message'}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Only show separate subject if it actually differs from message body */}
                  {selectedInquiry.shortDescription &&
                    selectedInquiry.message &&
                    selectedInquiry.shortDescription.trim().toLowerCase() !==
                      selectedInquiry.message.trim().toLowerCase() && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">Subject</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', mt: 0.3 }}>
                          {selectedInquiry.shortDescription}
                        </Typography>
                      </Box>
                    )}

                  <Box>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        color: '#1f2937',
                      }}
                    >
                      {selectedInquiry.message || selectedInquiry.shortDescription || 'No message content provided.'}
                    </Paper>
                  </Box>
                </CardContent>
              </Card>

              {/* Release Audit Details (if released) */}
              {selectedInquiry.isReleased && (
                <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AdminPanelSettings sx={{ color: '#16a34a', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534' }}>
                        {t('contactInquiries.releaseInfo') || 'Release Audit Log'}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Released At</Typography>
                        <Typography variant="body2" fontWeight={600} color="#166534">
                          {formatDateTime(selectedInquiry.releasedAtUtc)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Released By</Typography>
                        <Typography variant="body2" fontWeight={600} color="#166534">
                          {selectedInquiry.releasedByAdminName || 'Administrator'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {selectedInquiry && !selectedInquiry.isReleased && canManage ? (
            <Button
              variant="contained"
              startIcon={<ForwardToInbox sx={{ fontSize: '16px !important' }} />}
              onClick={() => {
                handleOpenReleaseDialog(selectedInquiry);
              }}
              sx={{
                backgroundColor: '#0F3D3E',
                color: '#ffffff',
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                px: 2.5,
                '&:hover': {
                  backgroundColor: '#0a2829',
                },
              }}
            >
              {t('contactInquiries.btnRelease') || 'Release Inquiry Now'}
            </Button>
          ) : (
            <Box />
          )}

          <Button
            variant="outlined"
            onClick={handleCloseDetails}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#e5e7eb',
              color: '#374151',
            }}
          >
            {t('common.cancel') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactInquiries;
